import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { useCompleteProcessing } from '@/models/article';
import { getAnalysisExchangeTypes } from '@/models/workflow';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import { ArticleActionProps } from './ArticleActions';
import { WorkshopPaymentStrategyInput } from './RepairArticleAction';

export const AdjustServiceDuringProcessingAction = ({ onActionDone }: ArticleActionProps) => {
  const { article, state, view, hasError } = useArticleContext();
  const { workflow, view: requestView } = useRequestContext();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const shouldShowPaymentStrategyInput =
    !article.workshopPaymentStrategy &&
    article.workshopOrganization?.workshopConfig?.payoutStrategy === 'handled-by-organization';

  const { mutateAsync: completeProcessing, isPending } = useCompleteProcessing({
    articleId: article.id,
  });

  const { t } = useLingui();

  const confirmServices = async () => {
    try {
      await completeProcessing({
        mode: 'validate-service-adjustment',
      });

      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  const handlePress = async () => {
    setApiError(null);

    const { hasError } = state.errors.check({
      defects: view.defects.shown
        ? {
            defects: {
              missingDescription: view.defects.fields.description.editable,
              missingPhotos:
                view.defects.fields.photo.shown &&
                view.defects.fields.photo.editable &&
                view.defects.fields.photo.required,
              missingContext:
                view.defects.fields.context.shown &&
                view.defects.fields.context.editable &&
                view.defects.fields.context.required,
              missingActions:
                view.defects.fields.toBeRepaired.shown &&
                view.defects.fields.toBeRepaired.editable &&
                view.services.actions.shown,
              missingExceptionalCoverageReason:
                view.defects.fields.brandResponsibility.exceptionalCoverage.enabled &&
                view.defects.fields.brandResponsibility.exceptionalCoverage.toggle.editable,
            },
            noDefects: view.defects.required,
          }
        : undefined,
      services: {
        actions: view.services.actions.shown
          ? {
              actions: {
                missingDescription: view.services.actions.fields.description.editable,
                missingPhotos:
                  view.services.actions.fields.photo.shown &&
                  view.services.actions.fields.photo.editable &&
                  view.services.actions.fields.photo.required,
                missingRefashionPhoto:
                  view.services.actions.fields.photo.shown &&
                  view.services.actions.fields.photo.editable,
                missingContext:
                  view.services.actions.fields.context.shown &&
                  view.services.actions.fields.context.editable &&
                  view.services.actions.fields.context.required,
                missingWorkshopPrice:
                  requestView.workshopPrice.shown &&
                  view.services.actions.fields.workshopPrice.editable &&
                  view.services.actions.fields.workshopPrice.required,
                missingOrganizationPrice:
                  requestView.organizationPrice.shown &&
                  view.services.actions.fields.organizationPrice.editable &&
                  view.services.actions.fields.organizationPrice.required,
                workshopPriceUpdateInProgress:
                  requestView.workshopPrice.shown &&
                  view.services.actions.fields.workshopPrice.editable,
                organizationPriceUpdateInProgress:
                  requestView.organizationPrice.shown &&
                  view.services.actions.fields.organizationPrice.editable,
                spareParts: view.services.actions.fields.spareParts.shown
                  ? {
                      quantityIsNull: view.services.actions.fields.spareParts.quantity.editable,
                      missingWorkshopPrice:
                        requestView.workshopPrice.shown &&
                        view.services.actions.fields.spareParts.workshopPrice.editable &&
                        view.services.actions.fields.spareParts.workshopPrice.required,
                      missingOrganizationPrice:
                        requestView.organizationPrice.shown &&
                        view.services.actions.fields.spareParts.organizationPrice.editable &&
                        view.services.actions.fields.spareParts.organizationPrice.required,
                      workshopPriceUpdateInProgress:
                        requestView.workshopPrice.shown &&
                        view.services.actions.fields.spareParts.workshopPrice.editable,
                      organizationPriceUpdateInProgress:
                        requestView.organizationPrice.shown &&
                        view.services.actions.fields.spareParts.organizationPrice.editable,
                    }
                  : undefined,
                additionalInfo: {
                  missingValue: view.services.actions.fields.additionalInfo.editable,
                },
              },
              noActions: view.services.actions.canAddAction,
            }
          : undefined,
        exchange: view.services.exchange.shown
          ? {
              missingExchangeType: !!getAnalysisExchangeTypes({ workflow, article })?.length,
              missingReplacementProduct: view.services.exchange.replacementProduct.editable,
              missingReplacementProductColor: view.details.color.shown,
              missingReplacementProductSize: view.details.size.shown,
              missingReplacementUniqueArticle:
                view.services.exchange.replacementUniqueArticle.shown &&
                view.services.exchange.replacementUniqueArticle.editable &&
                view.services.exchange.replacementUniqueArticle.required,
              missingWorkshopPrice:
                requestView.workshopPrice.shown &&
                view.services.exchange.workshopPrice.editable &&
                view.services.exchange.workshopPrice.required,
              workshopPriceUpdateInProgress:
                requestView.workshopPrice.shown && view.services.exchange.workshopPrice.editable,
              missingOrganizationPrice:
                requestView.organizationPrice.shown &&
                view.services.exchange.organizationPrice.editable &&
                view.services.exchange.organizationPrice.required,
              organizationPriceUpdateInProgress:
                requestView.organizationPrice.shown &&
                view.services.exchange.organizationPrice.editable,
            }
          : undefined,
      },
    });

    if (hasError) {
      return;
    }

    if (shouldShowPaymentStrategyInput) {
      setIsDialogOpen(true);
    } else {
      await confirmServices();
    }
  };

  return (
    <>
      <Footer>
        {(hasError || !!apiError) && (
          <FooterError>
            {hasError && (
              <Trans id="article.actions.adjust_service_during_processing.errors">
                Please fill the missing information in order to complete the service adjustment
              </Trans>
            )}
            {!!apiError && (
              <Paragraph weight="medium" color="danger">
                {apiError}
              </Paragraph>
            )}
          </FooterError>
        )}
        <FooterButtons>
          <Button variant="primary" size="medium" onPress={handlePress} isLoading={isPending}>
            <Trans id="article.actions.adjust_service_during_processing.confirm">
              Confirm services
            </Trans>
          </Button>
        </FooterButtons>
      </Footer>
      <Dialog
        title={
          <Trans id="article.actions.adjust_service_during_processing.confirm">
            Confirm services
          </Trans>
        }
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      >
        <AdjustServiceDuringProcessingDialogContent
          onActionDone={() => {
            setIsDialogOpen(false);
            onActionDone();
          }}
        />
      </Dialog>
    </>
  );
};

const AdjustServiceDuringProcessingDialogContent = ({
  onActionDone,
}: {
  onActionDone: () => void;
}) => {
  const { article } = useArticleContext();
  const { t } = useLingui();

  const [paymentStrategy, setPaymentStrategy] = useState(article.workshopPaymentStrategy);
  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | undefined>();

  const { mutateAsync: completeProcessing, isPending } = useCompleteProcessing({
    articleId: article.id,
  });

  const hasError = !paymentStrategy;

  const submit = async () => {
    setShowErrors(true);

    if (hasError) {
      return;
    }

    try {
      await completeProcessing({
        mode: 'validate-service-adjustment',
        paymentStrategy,
      });

      onActionDone();
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <main>
        <Stack gap="1rem">
          <Paragraph>
            <Trans id="article.actions.adjust_service_during_processing.confirm.text">
              By confirming, you acknowledge that the item and services match what was originally
              requested.
            </Trans>
          </Paragraph>
          <WorkshopPaymentStrategyInput
            paymentStrategy={paymentStrategy}
            setPaymentStrategy={setPaymentStrategy}
          />
        </Stack>
      </main>
      <Footer>
        {(!!apiError || (showErrors && hasError)) && (
          <FooterError>
            {apiError}
            {showErrors && hasError && (
              <Trans id="article.actions.processing.confirm.error">
                Please fill the missing information
              </Trans>
            )}
          </FooterError>
        )}
        <FooterButtons>
          <Button variant="primary" size="medium" onPress={submit} isLoading={isPending}>
            <Trans id="article.actions.adjust_service_during_processing.confirm">
              Confirm services
            </Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
